import Breadcrumbs from "components/Common/Breadcrumb";
import ClickableOrderCodes from "components/shared-components/ClickableOrderCodes";
import { getTicketRowImages } from "helpers/ticketResourceImage";
import { useTicketDetail, useTicketHistory } from "hooks";
import React, { useMemo, useState } from "react";
import MetaTags from "react-meta-tags";
import { useParams } from "react-router-dom";
import { Card, CardBody, Col, Form, FormGroup, Input, Label, Row, Spinner } from "reactstrap";
import {
  formatTicketHistoryAction,
  formatTicketStatus,
  getTicketSaleNamesFullText,
} from "./utils";
import TicketImagesModal from "./TicketImagesModal";
import { useTicketRouteGuard } from "./useTicketRouteGuard";
import "./TicketManagement.scss";

const EMPTY_VALUE = "—";

const formatDateTime = value => {
  if (value == null || value === "") {
    return EMPTY_VALUE;
  }
  const num = Number(value);
  const date = Number.isFinite(num) ? new Date(num * 1000) : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return EMPTY_VALUE;
  }
  const pad = n => String(n).padStart(2, "0");
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const TicketDetailPage = () => {
  const { isAllowed, isMyTicketsPath } = useTicketRouteGuard();
  const { ticketId } = useParams();
  const normalizedTicketId = Number(ticketId);
  if (!isAllowed) {
    return null;
  }

  const {
    data: ticketDetail,
    isLoading: isLoadingTicketDetail,
    isError: isTicketDetailError,
  } = useTicketDetail(normalizedTicketId);

  const {
    data: ticketHistory,
    isLoading: isLoadingTicketHistory,
  } = useTicketHistory(normalizedTicketId);

  const ticketStatus = useMemo(
    () => formatTicketStatus(ticketDetail?.status),
    [ticketDetail?.status]
  );

  const ticketImages = useMemo(() => getTicketRowImages(ticketDetail), [ticketDetail]);
  const historyRows = useMemo(
    () => (Array.isArray(ticketHistory) ? ticketHistory : []),
    [ticketHistory]
  );
  const [isOpenImagesModal, setIsOpenImagesModal] = useState(false);

  const saleNamesText = useMemo(
    () => getTicketSaleNamesFullText(ticketDetail),
    [ticketDetail]
  );

  const isLoading = isLoadingTicketDetail || isLoadingTicketHistory;

  return (
    <React.Fragment>
      <div className="page-content page-ticket-management">
        <MetaTags>
          <title>Chi tiết ticket</title>
        </MetaTags>
        <div className="container-fluid">
          <Breadcrumbs
            title="Ticket"
            heading={`Chi tiết ticket #${ticketDetail?.code ?? ""}`}
            breadcrumbItem="Chi tiết ticket"
          />

          {isLoading ? (
            <Card>
              <CardBody className="d-flex justify-content-center py-5">
                <Spinner color="primary" />
              </CardBody>
            </Card>
          ) : isTicketDetailError || !ticketDetail ? (
            <Card>
              <CardBody className="text-danger">Không tìm thấy thông tin ticket.</CardBody>
            </Card>
          ) : (
            <>
              <Card>
                <CardBody>
                  <h5 className="mb-3">Thông tin ticket</h5>
                  <Form className="OrderDetailForm">
                    <Row xs={1} md={2}>
                      <Col>
                        <FormGroup>
                          <Label htmlFor="code">Mã ticket</Label>
                          <Input
                            id="code"
                            type="text"
                            value={ticketDetail.code ?? EMPTY_VALUE}
                            readOnly
                          />
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup>
                          <Label htmlFor="order_ref_code">Mã tham chiếu</Label>
                          <Input
                            id="order_ref_code"
                            type="text"
                            value={ticketDetail.order_ref_code ?? EMPTY_VALUE}
                            readOnly
                          />
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup>
                          <Label htmlFor="receive_bus_order_ref_code">Mã đơn lấy hàng</Label>
                          <Input
                            id="receive_bus_order_ref_code"
                            type="text"
                            value={ticketDetail.receive_bus_order_ref_code ?? EMPTY_VALUE}
                            readOnly
                          />
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup>
                          <Label htmlFor="ticket-name">Tên ticket</Label>
                          <Input
                            id="ticket-name"
                            type="text"
                            value={ticketDetail.name ?? EMPTY_VALUE}
                            readOnly
                          />
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup>
                          <Label htmlFor="ticket-status">Trạng thái</Label>
                          <Input
                            id="ticket-status"
                            type="text"
                            value={ticketStatus.label || EMPTY_VALUE}
                            readOnly
                          />
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup>
                          <Label htmlFor="ticket-maker">Người tạo</Label>
                          <Input
                            id="ticket-maker"
                            type="text"
                            value={ticketDetail.maker?.display_name ?? EMPTY_VALUE}
                            readOnly
                          />
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup>
                          <Label htmlFor="ticket-maker-date">Ngày tạo</Label>
                          <Input
                            id="ticket-maker-date"
                            type="text"
                            value={formatDateTime(ticketDetail.maker_date)}
                            readOnly
                          />
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup>
                          <Label htmlFor="ticket-hub-name">Center/Hub</Label>
                          <Input
                            id="ticket-hub-name"
                            type="text"
                            value={ticketDetail.hub?.name ?? EMPTY_VALUE}
                            readOnly
                          />
                        </FormGroup>
                      </Col>
                      <Col>
                        <FormGroup>
                          <Label htmlFor="ticket-sale">Sale & Sales team</Label>
                          <div
                            id="ticket-sale"
                            className="form-control ticket-detail-multiline-field"
                          >
                            {saleNamesText || EMPTY_VALUE}
                          </div>
                        </FormGroup>
                      </Col>
                      <Col md={12}>
                        <FormGroup>
                          <Label htmlFor="ticket-description">Mô tả</Label>
                          <Input
                            id="ticket-description"
                            type="textarea"
                            rows={5}
                            value={ticketDetail.note ?? EMPTY_VALUE}
                            readOnly
                          />
                        </FormGroup>
                      </Col>
                      <Col md={12}>
                        <FormGroup className="mb-0">
                          <div className="d-flex align-items-center gap-2">
                            <Label className="mb-0">Ảnh đính kèm</Label>
                            {ticketImages.length ? (
                              <button
                                type="button"
                                className="btn btn-link p-0 border-0 bg-transparent"
                                onClick={() => setIsOpenImagesModal(true)}
                                title={
                                  ticketImages.length > 1
                                    ? `Xem ảnh đính kèm (${ticketImages.length} ảnh)`
                                    : "Xem ảnh đính kèm"
                                }
                              >
                                <i className="fas fa-image fa-lg text-primary" aria-hidden />
                              </button>
                            ) : null}
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>
                  </Form>
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <h5 className="mb-3">Lịch sử ticket</h5>
                  <div className="ticket-history-list">
                    <div className="ticket-history-list__header">
                      <span>No</span>
                      <span>Thời gian</span>
                      <span>Người thực hiện</span>
                      <span>Hành động</span>
                      <span>Ghi chú</span>
                    </div>
                    {historyRows.length ? (
                      historyRows.map((item, index) => (
                        <div
                          key={`ticket-history-${item.id ?? index}`}
                          className="ticket-history-list__row"
                        >
                          <span>{index + 1}</span>
                          <span>{formatDateTime(item.maker_date)}</span>
                          <span>{item.maker_name ?? "Hệ thống"}</span>
                          <span>{formatTicketHistoryAction(item.action)}</span>
                          <span>
                            <ClickableOrderCodes
                              text={item.note ?? EMPTY_VALUE}
                              linkClassName="ticket-history-order-link"
                              isAllowOrderLink={!isMyTicketsPath}
                            />
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="ticket-history-list__empty">Chưa có lịch sử ticket.</div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </>
          )}
        </div>
      </div>
      <TicketImagesModal
        isOpen={isOpenImagesModal}
        toggle={() => setIsOpenImagesModal(false)}
        ticket={ticketDetail}
      />
    </React.Fragment>
  );
};

export default TicketDetailPage;
